// ManageCalendars.js
import { useEffect, useMemo, useState } from 'react';
import { Spinner, Flex, Divider, Heading, WrapItem, Tooltip } from '@chakra-ui/react';
import { useCalendars } from './useCalendars';
import useActivities from '../Activities/useActivities';
import CalendarForm from './CalendarForm';
import CalendarList from './CalendarList';
import CalendarConfig from './CalendarConfig';
import ActivityCard from './ActivityCard';
import { ReactSortable } from "react-sortablejs";
import styled from "styled-components";

function ManageCalendars() {
  const [selectedCalendarId, setSelectedCalendarId] = useState(null)

  const { calendars, isLoading, createCalendar, updateCalendar, deleteCalendar, handleReorderCalendars } = useCalendars();
  const { activities } = useActivities();

  // Derive the selected calendar from the live snapshot data so edits made
  // here (or by other admins) never leave a stale object on screen.
  const selectedCalendar = useMemo(
    () => (calendars || []).find((cal) => cal.id === selectedCalendarId) || null,
    [calendars, selectedCalendarId]
  );

  // Local list for the sortable activity bank. react-sortablejs needs a
  // setList it can call with drag bookkeeping; content is re-synced from the
  // calendarActivities snapshot and never written back to Firestore.
  const [bankList, setBankList] = useState([])
  useEffect(() => {
    setBankList(activities.filter((activity) => activity.deleted === false));
  }, [activities]);

  const handleCreateCalendar = async (values) => {
    await createCalendar(values);
  };

  return (
    <>
    <Flex flexDir='row' mb='2'>
      {/* Left Column - Calendars */}
      <Flex mr='5' flexDir='column' width='20vw' align="start">
        <Tooltip placement='top' label="Calendars are created by giving them a lower and upper range. The users funded units goal will determine which calendar is displayed to them." aria-label='A tooltip'>
            <Heading size='sm' textTransform={'uppercase'}>Add A Calendar</Heading>
        </Tooltip>
        <CalendarForm onSubmit={handleCreateCalendar} isSubmitting={isLoading} />

        {isLoading ? 
          <Spinner />
         : 
            <>
            {calendars && 
                <CalendarList 
                    calendars={calendars}
                    selectedCalendar={selectedCalendar}
                    setSelectedCalendar={(calendar) => setSelectedCalendarId(calendar ? calendar.id : null)}
                    updateCalendar={updateCalendar}
                    deleteCalendar={deleteCalendar}
                    handleReorderCalendars={handleReorderCalendars}
                    />
            }
            </>

        }
      </Flex>
      <Flex flexDir={'column'}>
        <Heading size='sm' textTransform={'uppercase'}>Activity Bank</Heading>
        <StyledReactSortable
              list={bankList}
              // The bank is clone-only (put: false, sort: false), so sortable's
              // setList calls are always flag churn on the same item refs — bail
              // out to avoid cascading re-renders during drags.
              setList={(newList) => setBankList((prev) => {
                  const sameItems = newList.length === prev.length && newList.every((item, i) => item === prev[i])
                  return sameItems ? prev : newList
              })}
              animation={200}
              sort={false}
              clone={(item) => ({ id: item.id, title: item.title, color: item.color, description: item.description })}
              group={{ name: "activities", pull: "clone", put: false }}
          >
              {bankList?.map((activity, i) => {
                    return(
                      <WrapItem mr='1' width='225px' key={`wrap-item-activity-bank-${i}-${activity.id}`}>
                        <ActivityCard width='225px' activity={activity} activityList={bankList} truncateText/>
                      </WrapItem>
                    )
                })}

          </StyledReactSortable>
        </Flex>
    </Flex>
    <Divider mt='2' mb='2' />
    <Flex flexDir='row'>
      <Flex mr='2' flexDir='column' width='55vw' align="start">
        {selectedCalendar &&
        <>
         
          <Flex  mb='2'>       
            <Heading size='sm' textTransform={'uppercase'}>{selectedCalendar.lower} - {selectedCalendar.upper}</Heading>
          </Flex>
          <CalendarConfig 
            key={`calendar-config-${selectedCalendar.id}`} 
            calendar={selectedCalendar} 
            allActivities={bankList}
            updateCalendar={updateCalendar}
          />

        </>}
        </Flex>
    </Flex>
    </>
  );
}

export default ManageCalendars;

const StyledReactSortable = styled(ReactSortable)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;
